/*
 * Copyright (C) 2009-2025 SonarSource Sàrl
 * All rights reserved
 * mailto:info AT sonarsource DOT com
 */

import {
  Link,
  LinkHighlight,
  MessageCallout,
  MessageVariety,
  Text,
} from '@sonarsource/echoes-react';
import { ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { StaleTime } from '~shared/queries/common';
import { DocLink } from '~sq-server-commons/helpers/doc-links';
import { useDocUrl } from '~sq-server-commons/helpers/docs';
import { useProjectContainsAiCodeQuery } from '~sq-server-commons/queries/ai-code-assurance';
import { BaseProfile } from '~sq-server-commons/types/quality-profiles';
import { Component } from '~sq-server-commons/types/types';
import { useAiCodeProfileSuggestion } from '../../hooks/useAiCodeProfileSuggestion';

interface Props {
  component: Component;
  profiles: BaseProfile[];
}

export default function ProfileAICodeSuggestionBanner({
  profiles,
  component,
}: Readonly<Props>): ReactNode {
  const intl = useIntl();
  const profilesDocUrl = useDocUrl(DocLink.AiCodeAssuranceProfiles);
  const { data: containsAiCode } = useProjectContainsAiCodeQuery(
    { project: component },
    {
      staleTime: StaleTime.LONG,
    },
  );
  const { shouldShowProfileSuggestion, setShowProfileAiSuggestionBanner } =
    useAiCodeProfileSuggestion({
      component,
      profiles,
      containsAiCode,
    });

  if (!shouldShowProfileSuggestion) {
    return null;
  }

  return (
    <MessageCallout
      className="sw-mt-6"
      onDismiss={() => {
        setShowProfileAiSuggestionBanner(false);
      }}
      title={intl.formatMessage({ id: 'project_quality_profile.contains_ai_code.title' })}
      variety={MessageVariety.Info}
    >
      <div className="sw-max-w-[675px]">
        <FormattedMessage
          id="project_quality_profile.contains_ai_code.description"
          values={{
            p: (text) => (
              <Text as="p" className="sw-mt-3">
                <Link enableOpenInNewTab highlight={LinkHighlight.Default} to={profilesDocUrl}>
                  {text}
                </Link>
              </Text>
            ),
          }}
        />
      </div>
    </MessageCallout>
  );
}
